/**
 * YouTubeTheaterPlayer — Atmosphere A1.
 *
 * Singleton wrapper over the YouTube IFrame Player API. The official
 * `https://www.youtube.com/iframe_api` script is loaded on first play()
 * via <script> injection; no npm package. A hidden 1x1 iframe attached
 * to document.body satisfies the autoplay-policy requirement that the
 * player exists in the document context.
 *
 * Cue map shape (loaded once, validated):
 *   { schema_version, source_playlist_id, cues: {
 *       [cueId]: { video_id, start_seconds, end_seconds, volume,
 *                  fade_in_ms, notes }
 *   } }
 *
 * Failure mode (planner-track A1 risk #1): if the YouTube API is
 * unreachable, the cue's video_id is null, or YT.Player rejects the
 * embed, every public method resolves silently (Promise<void>) — the
 * one-time "Theme music unavailable" toast is the caller's
 * responsibility via the `onUnavailable` constructor hook.
 *
 * Voice ducking (planner-track A1 risk #4): duck(targetVolume, ms)
 * gracefully attenuates while voice is active; the M3 voice-binding
 * will call it. crossfadeTo(cueId, ms) atomically swaps cues without
 * a silence gap.
 */

export interface YouTubeCueConfig {
  video_id: string | null;
  start_seconds: number;
  end_seconds: number | null;
  volume: number;
  fade_in_ms: number;
  notes?: string;
}

export interface YouTubeCueMap {
  schema_version: string;
  source_playlist_id: string;
  cues: Record<string, YouTubeCueConfig>;
}

export interface YouTubeTheaterPlayerOptions {
  cueMap: YouTubeCueMap;
  onUnavailable?: () => void;
}

interface YTPlayer {
  loadVideoById: (config: { videoId: string; startSeconds?: number; endSeconds?: number }) => void;
  pauseVideo: () => void;
  stopVideo: () => void;
  setVolume: (volume: number) => void;
  getVolume: () => number;
  destroy: () => void;
}

interface YTPlayerCtorOptions {
  width: number;
  height: number;
  videoId?: string;
  playerVars?: Record<string, unknown>;
  events?: {
    onReady?: (event: { target: YTPlayer }) => void;
    onError?: (event: { data: number }) => void;
  };
}

interface YTGlobal {
  Player: new (host: HTMLElement | string, options: YTPlayerCtorOptions) => YTPlayer;
}

interface YTWindow {
  YT?: YTGlobal;
  onYouTubeIframeAPIReady?: () => void;
}

const API_SCRIPT_SRC = 'https://www.youtube.com/iframe_api';
const HOST_ELEMENT_ID = 'br-yt-theater-host';

export class YouTubeTheaterPlayer {
  private readonly cueMap: YouTubeCueMap;
  private readonly onUnavailable: () => void;
  private player: YTPlayer | null = null;
  private apiReadyPromise: Promise<YTGlobal> | null = null;
  private unavailableReported = false;

  constructor(options: YouTubeTheaterPlayerOptions) {
    this.cueMap = options.cueMap;
    this.onUnavailable = options.onUnavailable ?? (() => {});
  }

  async play(cueId: string): Promise<void> {
    const cue = this.cueMap.cues[cueId];
    if (cue === undefined || cue.video_id === null) {
      this.reportUnavailable();
      return;
    }
    const yt = await this.ensureApi();
    if (yt === null) {
      return;
    }
    const player = await this.ensurePlayer(yt);
    if (player === null) {
      return;
    }
    player.setVolume(0);
    player.loadVideoById({
      videoId: cue.video_id,
      startSeconds: cue.start_seconds,
      endSeconds: cue.end_seconds ?? undefined,
    });
    await this.fadeVolumeTo(cue.volume, cue.fade_in_ms);
  }

  async fadeOut(durationMs: number): Promise<void> {
    if (this.player === null) {
      return;
    }
    await this.fadeVolumeTo(0, durationMs);
    this.player.stopVideo();
  }

  async duck(targetVolume: number, durationMs: number): Promise<void> {
    if (this.player === null) {
      return;
    }
    await this.fadeVolumeTo(targetVolume, durationMs);
  }

  async crossfadeTo(cueId: string, durationMs: number): Promise<void> {
    const cue = this.cueMap.cues[cueId];
    if (cue === undefined || cue.video_id === null) {
      this.reportUnavailable();
      return;
    }
    if (this.player === null) {
      await this.play(cueId);
      return;
    }
    await this.fadeVolumeTo(0, durationMs / 2);
    this.player.loadVideoById({
      videoId: cue.video_id,
      startSeconds: cue.start_seconds,
      endSeconds: cue.end_seconds ?? undefined,
    });
    await this.fadeVolumeTo(cue.volume, durationMs / 2);
  }

  private reportUnavailable(): void {
    if (this.unavailableReported) {
      return;
    }
    this.unavailableReported = true;
    this.onUnavailable();
  }

  private async ensureApi(): Promise<YTGlobal | null> {
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      this.reportUnavailable();
      return null;
    }
    const w = window as unknown as YTWindow;
    if (w.YT !== undefined) {
      return w.YT;
    }
    if (this.apiReadyPromise === null) {
      this.apiReadyPromise = new Promise<YTGlobal>((resolve, reject) => {
        const existing = w.onYouTubeIframeAPIReady;
        w.onYouTubeIframeAPIReady = () => {
          if (existing !== undefined) {
            existing();
          }
          if (w.YT !== undefined) {
            resolve(w.YT);
          } else {
            reject(new Error('youtube_iframe_api_unavailable'));
          }
        };
        const script = document.createElement('script');
        script.src = API_SCRIPT_SRC;
        script.async = true;
        script.onerror = () => reject(new Error('youtube_iframe_api_load_failed'));
        document.body.appendChild(script);
      });
    }
    try {
      return await this.apiReadyPromise;
    } catch {
      this.reportUnavailable();
      return null;
    }
  }

  private async ensurePlayer(yt: YTGlobal): Promise<YTPlayer | null> {
    if (this.player !== null) {
      return this.player;
    }
    let host = document.getElementById(HOST_ELEMENT_ID);
    if (host === null) {
      host = document.createElement('div');
      host.id = HOST_ELEMENT_ID;
      host.style.width = '1px';
      host.style.height = '1px';
      host.style.position = 'fixed';
      host.style.left = '-9999px';
      host.style.top = '-9999px';
      host.setAttribute('aria-hidden', 'true');
      document.body.appendChild(host);
    }
    return new Promise<YTPlayer | null>((resolve) => {
      try {
        const created = new yt.Player(host, {
          width: 1,
          height: 1,
          playerVars: {
            autoplay: 0,
            controls: 0,
            disablekb: 1,
            modestbranding: 1,
            // 2026-06-05 Bug Hunt Finding #3: pin the postMessage target
            // origin to our parent host so YouTube's www-widgetapi.js
            // stops logging "target origin provided does not match the
            // recipient window's origin" warnings on every player init.
            // jsdom-friendly (window.location exists but origin may be
            // 'http://localhost'); production paths get the real host.
            origin:
              typeof window !== 'undefined'
                ? window.location.origin
                : '',
          },
          events: {
            onReady: (event) => {
              this.player = event.target;
              resolve(event.target);
            },
            onError: () => {
              this.reportUnavailable();
              resolve(null);
            },
          },
        });
        // Keep a reference even before onReady fires so a sync error
        // path (constructor throw) is reported via the catch below.
        this.player = created;
      } catch {
        this.reportUnavailable();
        resolve(null);
      }
    });
  }

  private async fadeVolumeTo(target: number, durationMs: number): Promise<void> {
    if (this.player === null) {
      return;
    }
    const player = this.player;
    const start = player.getVolume();
    const clampedTarget = Math.max(0, Math.min(100, Math.round(target)));
    if (durationMs <= 0 || start === clampedTarget) {
      player.setVolume(clampedTarget);
      return;
    }
    const steps = Math.max(1, Math.floor(durationMs / 50));
    const stepMs = durationMs / steps;
    for (let i = 1; i <= steps; i += 1) {
      const progress = i / steps;
      const next = Math.round(start + (clampedTarget - start) * progress);
      player.setVolume(next);
      await new Promise<void>((resolve) => {
        setTimeout(resolve, stepMs);
      });
    }
  }
}

let singleton: YouTubeTheaterPlayer | null = null;

export function getYouTubeTheaterPlayer(
  options: YouTubeTheaterPlayerOptions,
): YouTubeTheaterPlayer {
  if (singleton === null) {
    singleton = new YouTubeTheaterPlayer(options);
  }
  return singleton;
}

export function resetYouTubeTheaterPlayerForTests(): void {
  singleton = null;
}
